import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";

const InvoicePreview = ({
  showModal,
  onClose,
  pdfUrl,
  htmlContent,
  guestDetails,
  type = "proforma",
  outputType,
  handleSwitchView,
  loading,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);

  // Handle initial loading state when modal opens
  useEffect(() => {
    if (showModal && !pdfUrl && !htmlContent) {
      setIsLoading(true);
    } else if (showModal && (pdfUrl || htmlContent)) {
      setIsLoading(false);
      setIsSwitching(false);
    }
  }, [showModal, pdfUrl, htmlContent]);

  // Disable background scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100vh";
    } else {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    };
  }, [showModal]);

  // Handle modal close and cleanup
  const handleClose = () => {
    onClose();
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setIsSwitching(false);
  };

  const handleDownloadInvoice = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = type === "proforma" ? "Proforma_Invoice.pdf" : "Invoice.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrintInvoice = () => {
    if (htmlContent) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Print ${type === "proforma" ? "Proforma" : ""} Invoice</title>
            <style>
              @page { size: A4; margin: 5mm; }
              body { margin: 0; padding: 0; }
              .a4-container {
                width: 210mm;
                min-height: 297mm;
                margin: 0 auto;
                box-sizing: border-box;
              }
            </style>
          </head>
          <body>
            <div class="a4-container">
              ${htmlContent}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  const handleViewSwitch = (newOutputType) => {
    setIsSwitching(true);
    handleSwitchView(newOutputType, () => {
      setIsLoading(true);
    });
  };

  const title = type === "proforma" ? "Proforma Invoice Preview" : "Invoice Preview";

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      keyboard={false}
      dialogClassName="invoice-preview-modal-dialog"
      backdropClassName="modal-backdrop"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header
        style={{
          flexShrink: 0,
          padding: "10px 20px",
          borderBottom: "1px solid #dee2e6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Modal.Title id="pdfPreviewModalLabel">{title}</Modal.Title>
   
      </Modal.Header>
      <Modal.Body
        style={{
          flex: 1,
          padding: 0,
          overflowY: "auto",
          height: "calc(100vh - 140px)",
        }}
      >
        {(loading || isLoading || isSwitching) ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <Spinner animation="border" role="status" style={{ width: "3rem", height: "3rem" }} />
            <p style={{ marginTop: "10px", fontSize: "16px" }}>
              {isSwitching ? "Switching view..." : "Loading..."}
            </p>
          </div>
        ) : htmlContent ? (
          <div
            style={{
              padding: "5mm",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "210mm",
                height: "auto",
                padding: "10px",
                boxSizing: "border-box",
                backgroundColor: "#fff",
                boxShadow: "0 0 5px rgba(0,0,0,0.1)",
              }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>
        ) : pdfUrl ? (
          <div
            style={{
              padding: "5mm",
              display: "flex",
              justifyContent: "center",
              height: "100%", // Ensure wrapper takes full modal body height
              overflowY: "auto", // Allow wrapper to scroll if iframe overflows
            }}
          >
            <iframe
              src={pdfUrl}
              style={{
                width: "210mm",
                height: "100%", // Take full height of wrapper
                border: "none",
              }}
              title={title}
              loading="lazy"
              scrolling="auto" // Enable iframe scrolling
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            No content to display
          </div>
        )}
      </Modal.Body>
      <Modal.Footer
        style={{
          flexShrink: 0,
          padding: "10px 20px",
          borderTop: "1px solid #dee2e6",
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "center",
          gap: "10px",
        }}
      >
        <Button
          variant={outputType === "pdf" ? "outline-primary" : "primary"}
          onClick={() => handleViewSwitch(outputType === "pdf" ? "html" : "pdf")}
          style={{ fontSize: "14px", padding: "5px 10px" }}
          disabled={loading || isLoading || isSwitching}
        >
          Switch to {outputType === "pdf" ? "HTML" : "PDF"} View
        </Button>
        {outputType === "pdf" ? (
          <Button
            variant="primary"
            onClick={handleDownloadInvoice}
            disabled={!pdfUrl || loading || isLoading || isSwitching}
            style={{ fontSize: "14px", padding: "5px 10px" }}
          >
            Download PDF
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handlePrintInvoice}
            disabled={!htmlContent || loading || isLoading || isSwitching}
            style={{ fontSize: "14px", padding: "5px 10px" }}
          >
            Print
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={handleClose}
          style={{ fontSize: "14px", padding: "5px 10px" }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Add custom styles for the modal dialog
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
  .invoice-preview-modal-dialog {
    width: 230mm;
    max-width: 100%;
    position: fixed;
    right: 0;
    height: 100vh;
    margin: 0;
  }
  .modal-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;
document.head.appendChild(styleSheet);

export default InvoicePreview;